import type { DataFileListItem, TrackContribution } from '@kangaroo/shared';
import axios from 'axios';

import { fetchDataFile, fetchDataFiles } from '../dataFiles';
import {
  buildDataFileSessionSubtitle,
  buildDataFileSessionTitle,
  resolveDataFileRecordedAtMs,
} from '../../lib/telemetrySessions';
import { formatCircuitPickerMenuLabel } from '../../lib/circuits';

export type SessionListItem = {
  id: string;
  title: string;
  subtitle: string;
  recordedAtMs: number | null;
  trackName: string | null;
  driverId: string | null;
  driverName: string | null;
  driverAvatarUrl: string | null;
  kartId?: string | null;
  kartNumber?: string | null;
  lapCount?: number | null;
  bestLapMs?: number | null;
  trackId?: string | null;
  contributionId?: string | null;
};

export type CompareSessionGroups = {
  matching: SessionListItem[];
  other: SessionListItem[];
};

export type SessionListPage = {
  sessions: SessionListItem[];
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  snapshotAnchor: string;
};

export type SessionListDirectory = Omit<SessionListPage, 'sessions'> & {
  sessions: SessionListItem[];
};

export type SessionListDirectoryResult = {
  directory: SessionListDirectory | null;
  error: string | null;
  cacheable: boolean;
};

export type SessionListPersistenceAttempt = {
  generation: number;
  teamId: string | null;
  pageOneSucceeded: boolean;
};

export type RequestedSessionSelection = {
  session: SessionListItem | null;
  error: string | null;
  pending: boolean;
};

export const DEMO_SESSION_ID = 'demo:a_0180';

export const DEMO_SESSION: SessionListItem = {
  id: DEMO_SESSION_ID,
  title: 'Demo session',
  subtitle: '13:30 · CWFULL · 13 laps',
  recordedAtMs: Date.UTC(2026, 5, 28, 13, 30, 10),
  trackName: 'CWFULL',
  driverId: null,
  driverName: 'Demo driver',
  driverAvatarUrl: null,
  kartNumber: null,
  lapCount: 13,
  bestLapMs: null,
  trackId: null,
  contributionId: null,
};

export function beginSessionListPersistenceAttempt(
  current: SessionListPersistenceAttempt,
  teamId: string | null,
): SessionListPersistenceAttempt {
  return {
    generation: current.generation + 1,
    teamId,
    pageOneSucceeded: false,
  };
}

export function completeSessionListPersistenceAttempt(
  current: SessionListPersistenceAttempt,
  request: { generation: number; teamId: string; page: number },
): SessionListPersistenceAttempt {
  if (
    request.page !== 1 ||
    request.generation !== current.generation ||
    request.teamId !== current.teamId
  ) {
    return current;
  }

  return { ...current, pageOneSucceeded: true };
}

export function sessionListPersistenceAttemptIsEligible(
  current: SessionListPersistenceAttempt,
  teamId: string | null | undefined,
) {
  return Boolean(teamId && current.teamId === teamId && current.pageOneSucceeded);
}

export function isDemoSessionId(sessionId: string | null | undefined) {
  return sessionId === DEMO_SESSION_ID;
}

export function normalizeTrackName(trackName: string | null | undefined) {
  return trackName?.trim().toLowerCase().replace(/\s+/g, ' ') || null;
}

export function sessionsShareAssignedCircuit(left: SessionListItem, right: SessionListItem) {
  // A contribution identifies the assigned layout/configuration. Venue names and
  // logger labels are not identities, and must not override conflicting IDs.
  if (left.contributionId || right.contributionId) {
    return Boolean(left.contributionId && left.contributionId === right.contributionId);
  }
  return Boolean(left.trackId && left.trackId === right.trackId);
}

function withCircuitLabel(session: SessionListItem, trackName: string | null): SessionListItem {
  if (session.trackName === trackName) return session;
  const parts = session.subtitle.split(' · ');
  const oldParts = session.trackName?.split(' · ') ?? [];
  const index = oldParts.length
    ? parts.findIndex((_, start) => oldParts.every((part, offset) => parts[start + offset] === part))
    : -1;
  if (index >= 0) {
    parts.splice(index, oldParts.length, ...(trackName ? [trackName] : []));
  } else if (trackName) {
    const lapIndex = parts.findIndex((part) => /^\d+ laps?$/.test(part));
    parts.splice(lapIndex >= 0 ? lapIndex : parts.length, 0, trackName);
  }
  return { ...session, trackName, subtitle: parts.join(' · ') };
}

export function withAssignedCircuitLabel(
  session: SessionListItem,
  contributions: TrackContribution[],
): SessionListItem {
  if (session.contributionId) {
    const assigned = contributions.find((entry) => entry.id === session.contributionId);
    return withCircuitLabel(session, assigned
      ? formatCircuitPickerMenuLabel(assigned, { includeStatus: false })
      : session.trackName ?? 'Assigned circuit');
  }
  if (session.trackId) {
    const assigned = contributions.find((entry) => entry.trackId === session.trackId);
    return withCircuitLabel(session, assigned?.venue?.trim() || session.trackName || 'Assigned circuit');
  }
  return session;
}

export function buildCompareSessionGroups(
  sessions: SessionListItem[],
  primarySession: SessionListItem | null,
): CompareSessionGroups {
  if (!primarySession) {
    return { matching: [], other: [] };
  }

  const matching: SessionListItem[] = [];
  const other: SessionListItem[] = [];
  const matchingIds = new Set<string>();

  for (const session of sessions) {
    const isMatch =
      session.id === primarySession.id || sessionsShareAssignedCircuit(session, primarySession);

    if (isMatch) {
      matching.push(session);
      matchingIds.add(session.id);
    }
  }

  for (const session of sessions) {
    if (!matchingIds.has(session.id)) {
      other.push(session);
    }
  }

  return { matching, other };
}

export function filterSessionsByTrack(
  sessions: SessionListItem[],
  trackName: string | null | undefined,
) {
  const normalized = normalizeTrackName(trackName);
  if (!normalized) {
    return [];
  }

  return sessions.filter((session) => normalizeTrackName(session.trackName) === normalized);
}

export function pickLatestSessionByRecordedDate(sessions: SessionListItem[]) {
  if (sessions.length === 0) {
    return null;
  }

  return (
    [...sessions].sort((left, right) => {
      const leftMs = left.recordedAtMs ?? Number.NEGATIVE_INFINITY;
      const rightMs = right.recordedAtMs ?? Number.NEGATIVE_INFINITY;
      if (rightMs !== leftMs) {
        return rightMs - leftMs;
      }

      return right.id.localeCompare(left.id);
    })[0] ?? null
  );
}

function withSavedDriverName(
  session: SessionListItem,
  driverId: string | null,
  driverName: string | null,
  driverAvatarUrl: string | null = session.driverAvatarUrl,
) {
  const nextDriverName = driverName?.trim() || null;
  const parts = session.subtitle
    .split('·')
    .map((part) => part.trim())
    .filter(Boolean);
  const driverLabel = nextDriverName ? `Driver ${nextDriverName}` : 'Driver unassigned';
  const driverPartIndex = parts.findIndex((part) => part.toLowerCase().startsWith('driver '));

  if (driverPartIndex >= 0) {
    parts[driverPartIndex] = driverLabel;
  } else {
    parts.splice(parts.length > 0 ? 1 : 0, 0, driverLabel);
  }

  return {
    ...session,
    subtitle: parts.join(' · '),
    driverId,
    driverName: nextDriverName,
    driverAvatarUrl,
  };
}

const INCONSISTENT_SESSION_LIST_MESSAGE =
  'The session list changed while loading. Reload it to continue.';
export const SESSION_LIST_PAGE_SIZE = 100;

function sortSessions(sessions: SessionListItem[]) {
  return [...sessions].sort((left, right) => {
    const leftMs = left.recordedAtMs ?? Number.NEGATIVE_INFINITY;
    const rightMs = right.recordedAtMs ?? Number.NEGATIVE_INFINITY;
    if (rightMs !== leftMs) {
      return rightMs - leftMs;
    }

    return left.id.localeCompare(right.id);
  });
}

export function dataFileToSessionListItem(file: DataFileListItem): SessionListItem {
  const driverId = file.sessionAssignment ? file.sessionAssignment.driverId : file.driverId ?? null;
  const driverName = file.sessionAssignment
    ? file.sessionAssignment.driverName?.trim() || null
    : file.summary.driverName?.trim() || null;
  const bestLapMs = file.laps
    .map((lap) => lap.durationMs)
    .filter((durationMs): durationMs is number => durationMs !== null && durationMs > 0)
    .sort((left, right) => left - right)[0] ?? null;

  const loggerTrackName = file.summary.trackName?.trim() || null;
  const session = {
    id: file.id,
    title: buildDataFileSessionTitle(file),
    subtitle: buildDataFileSessionSubtitle(
      file,
      undefined,
      driverName || undefined,
    ),
    recordedAtMs: resolveDataFileRecordedAtMs(file),
    trackName: loggerTrackName,
    driverId,
    driverName,
    driverAvatarUrl: null,
    kartId: file.sessionContextSummary?.kartId ?? file.kartId ?? null,
    kartNumber: file.summary.kartNumber?.trim().replace(/^#/, '') || null,
    lapCount: file.sessionContextSummary?.circuitLapCount || file.summary.lapCount || file.laps.length,
    bestLapMs: file.sessionContextSummary?.circuitBestLapMs ?? bestLapMs,
    trackId: file.sessionContextSummary?.trackId ?? null,
    contributionId: file.sessionContextSummary?.contributionId ?? null,
  };
  return withCircuitLabel(
    session,
    file.sessionContextSummary?.trackName?.trim() || loggerTrackName,
  );
}

export async function loadRequestedSession(
  sessionId: string,
  fetchOne: (sessionId: string) => Promise<DataFileListItem> = fetchDataFile,
) {
  return dataFileToSessionListItem(await fetchOne(sessionId));
}

export function requestedSessionLoadErrorMessage(error: unknown) {
  if (!error) return null;
  if (axios.isAxiosError(error) && [403, 404].includes(error.response?.status ?? 0)) {
    return 'The requested telemetry session is unavailable or you do not have access.';
  }
  return error instanceof Error
    ? error.message
    : 'The requested telemetry session could not be loaded. Retry to continue.';
}

export function resolveRequestedSessionSelection(
  requestedSessionId: string | null,
  requestedSession: SessionListItem | undefined,
  loadError: string | null,
): RequestedSessionSelection {
  if (!requestedSessionId) {
    return { session: null, error: null, pending: false };
  }
  if (isDemoSessionId(requestedSessionId)) {
    return { session: DEMO_SESSION, error: null, pending: false };
  }
  if (loadError) {
    return { session: null, error: loadError, pending: false };
  }
  if (!requestedSession) {
    return { session: null, error: null, pending: true };
  }

  return requestedSession.id === requestedSessionId
    ? { session: requestedSession, error: null, pending: false }
    : {
        session: null,
        error: 'The requested telemetry session is unavailable or you do not have access.',
        pending: false,
      };
}

export function mergeRequestedSessionIntoDirectory(
  requestedSession: SessionListItem | undefined,
  directorySessions: SessionListItem[],
) {
  if (!requestedSession) {
    return directorySessions;
  }

  // The paginated directory includes the account's saved driver and circuit
  // assignments. Prefer that item when the detail endpoint returns the same
  // telemetry session with only its original logger metadata.
  const selectedSession = directorySessions.find(
    (session) => session.id === requestedSession.id,
  ) ?? requestedSession;

  return [
    selectedSession,
    ...directorySessions.filter((session) => session.id !== requestedSession.id),
  ];
}

export function resolveRequestedSessionExportError(
  requestedSessionId: string | null,
  selectedSessionId: string | null,
  exportError: unknown,
) {
  if (!requestedSessionId || selectedSessionId !== requestedSessionId || !exportError) {
    return null;
  }

  return exportError instanceof Error
    ? exportError.message
    : 'The requested telemetry session could not be loaded. Retry to continue.';
}

function assertValidSessionListPage(page: SessionListPage, expectedPage: number) {
  const expectedTotalPages = Math.max(Math.ceil(page.total / page.perPage), 1);
  const expectedLoadedCount = Math.min(page.page * page.perPage, page.total);

  if (
    page.page !== expectedPage ||
    page.page < 1 ||
    page.perPage < 1 ||
    page.perPage > 100 ||
    page.total < 0 ||
    page.totalPages !== expectedTotalPages ||
    page.page > page.totalPages ||
    page.sessions.length > page.perPage ||
    page.sessions.length !== expectedLoadedCount - (page.page - 1) * page.perPage ||
    !page.snapshotAnchor
  ) {
    throw new Error(INCONSISTENT_SESSION_LIST_MESSAGE);
  }
}

export function mergeSessionListPage(
  current: SessionListDirectory | null,
  page: SessionListPage,
): SessionListDirectory {
  const expectedPage = current ? current.page + 1 : 1;
  assertValidSessionListPage(page, expectedPage);

  if (
    current &&
    (current.perPage !== page.perPage ||
      current.total !== page.total ||
      current.totalPages !== page.totalPages ||
      current.snapshotAnchor !== page.snapshotAnchor)
  ) {
    throw new Error(INCONSISTENT_SESSION_LIST_MESSAGE);
  }

  const sessions = sortSessions([...(current?.sessions ?? []), ...page.sessions]);
  if (new Set(sessions.map((session) => session.id)).size !== sessions.length) {
    throw new Error(INCONSISTENT_SESSION_LIST_MESSAGE);
  }

  return {
    sessions,
    page: page.page,
    perPage: page.perPage,
    total: page.total,
    totalPages: page.totalPages,
    snapshotAnchor: page.snapshotAnchor,
  };
}

export function resolveSessionListDirectory(
  pages: SessionListPage[],
  cachedDirectory: SessionListDirectory | null,
) {
  const freshDirectory = pages.reduce<SessionListDirectory | null>(
    (directory, page) => mergeSessionListPage(directory, page),
    null,
  );

  if (
    freshDirectory &&
    cachedDirectory &&
    cachedDirectory.page > freshDirectory.page &&
    cachedDirectory.perPage === freshDirectory.perPage &&
    cachedDirectory.total === freshDirectory.total &&
    cachedDirectory.totalPages === freshDirectory.totalPages &&
    cachedDirectory.snapshotAnchor === freshDirectory.snapshotAnchor
  ) {
    const refreshedIds = new Set(freshDirectory.sessions.map(({ id }) => id));
    return {
      ...cachedDirectory,
      sessions: sortSessions([
        ...freshDirectory.sessions,
        ...cachedDirectory.sessions.filter(({ id }) => !refreshedIds.has(id)),
      ]),
    };
  }

  return freshDirectory;
}

export function resolveSessionListDirectoryResult(
  pages: SessionListPage[] | undefined,
  cachedDirectory: SessionListDirectory | null,
  hasSuccessfulRequest = false,
): SessionListDirectoryResult {
  if (!pages?.length) {
    return { directory: cachedDirectory, error: null, cacheable: false };
  }

  try {
    return {
      directory: resolveSessionListDirectory(pages, cachedDirectory),
      error: null,
      cacheable: hasSuccessfulRequest,
    };
  } catch (error) {
    return {
      directory: cachedDirectory,
      error: error instanceof Error ? error.message : 'Unable to load the session list.',
      cacheable: false,
    };
  }
}

export function sessionListLoadIsPending(
  requestedPageCount: number,
  resolvedPageCount: number,
  isValidating: boolean,
) {
  return isValidating && requestedPageCount > resolvedPageCount;
}

export function sessionListDirectoryHasMore(directory: SessionListDirectory | null) {
  return Boolean(directory && directory.page < directory.totalPages);
}

export function nextSessionListPageCount(
  requestedPageCount: number,
  directory: SessionListDirectory | null,
) {
  return Math.max(requestedPageCount, directory?.page ?? 0) + 1;
}

export async function fetchSessionListPage(
  page = 1,
  snapshotAnchor?: string,
): Promise<SessionListPage> {
  if (page > 1 && !snapshotAnchor) {
    throw new Error(INCONSISTENT_SESSION_LIST_MESSAGE);
  }

  const result = await fetchDataFiles({
    source: 'ALL',
    page,
    perPage: SESSION_LIST_PAGE_SIZE,
    detail: 'session-list',
    snapshot: page === 1 ? 'new' : snapshotAnchor,
  });

  if (!result.snapshot) {
    throw new Error(INCONSISTENT_SESSION_LIST_MESSAGE);
  }

  const sessions = result.data.map(dataFileToSessionListItem);

  return {
    sessions,
    page: result.page,
    perPage: result.perPage,
    total: result.total,
    totalPages: result.totalPages,
    snapshotAnchor: result.snapshot,
  };
}

export async function fetchSessionList(): Promise<SessionListItem[]> {
  const page = await fetchSessionListPage();

  if (page.sessions.length === 0) {
    return [DEMO_SESSION];
  }

  return sortSessions(page.sessions);
}

export function updateSessionListItemDriver(
  session: SessionListItem,
  driverId: string | null,
  driverName: string | null,
) {
  return withSavedDriverName(
    session,
    driverId,
    driverName,
    driverId === session.driverId ? session.driverAvatarUrl : null,
  );
}

export function updateSessionListItemAssignment(
  session: SessionListItem,
  update: {
    driverId?: string | null;
    driverName?: string | null;
    kartId?: string | null;
    kartNumber?: string | null;
    trackId?: string | null;
    contributionId?: string | null;
    trackName?: string | null;
    lapCount?: number;
    bestLapMs?: number | null;
  },
) {
  const withDriver = update.driverId === undefined
    ? session
    : updateSessionListItemDriver(session, update.driverId, update.driverName ?? null);
  return {
    ...withCircuitLabel(withDriver, update.trackName !== undefined ? update.trackName : withDriver.trackName),
    ...(update.trackId !== undefined ? { trackId: update.trackId } : {}),
    ...(update.contributionId !== undefined ? { contributionId: update.contributionId } : {}),
    ...(update.trackName !== undefined ? { trackName: update.trackName } : {}),
    ...(update.kartId !== undefined ? { kartId: update.kartId } : {}),
    ...(update.kartNumber !== undefined ? { kartNumber: update.kartNumber } : {}),
    ...(update.lapCount !== undefined ? { lapCount: update.lapCount } : {}),
    ...(update.bestLapMs !== undefined ? { bestLapMs: update.bestLapMs } : {}),
  };
}
